import React from 'react';
import { Box, Typography } from '@mui/material';
import DocumentImage from 'src/components/DocumentImage';

export type FormatKind = 'bold' | 'h1' | 'h2' | 'bullet' | 'numbered';

const BULLET_RE = /^- /;
const NUMBERED_RE = /^\d+\.\s/;
const IMAGE_RE = /^!\[([^\]]*)\]\((.+)\)\s*$/;
const DOC_URL_RE = /\/DownloadDocument\?(?:[^&]*&)*id=(\d+)/;

function parseImageUrl(url: string): { docId?: number; src?: string } | null {
  const docMatch = url.match(DOC_URL_RE);
  if (docMatch) return { docId: parseInt(docMatch[1], 10) };
  if (url.startsWith('blob:')) return { src: url };
  return null;
}

function renderInline(text: string): React.ReactNode[] {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? <strong key={i}>{p}</strong> : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

type ListBuf = { ordered: boolean; items: string[] };

export function renderCommentBody(body: string): JSX.Element {
  const lines = body.split('\n');
  const blocks: JSX.Element[] = [];
  let listBuf: ListBuf | null = null;

  const flushList = () => {
    if (!listBuf) return;
    const key = `list-${blocks.length}`;
    const items = listBuf.items.map((item, i) => (
      <li key={i} style={{ marginBottom: 2 }}>
        {renderInline(item)}
      </li>
    ));
    blocks.push(
      listBuf.ordered ? (
        <Box
          component="ol"
          key={key}
          sx={{ pl: 3, my: 0.5, '& li': { typography: 'body2' } }}
        >
          {items}
        </Box>
      ) : (
        <Box
          component="ul"
          key={key}
          sx={{ pl: 3, my: 0.5, '& li': { typography: 'body2' } }}
        >
          {items}
        </Box>
      )
    );
    listBuf = null;
  };

  lines.forEach((line, idx) => {
    const imageMatch = line.match(IMAGE_RE);
    if (imageMatch) {
      const parsed = parseImageUrl(imageMatch[2]);
      if (parsed) {
        flushList();
        blocks.push(
          <DocumentImage
            key={idx}
            docId={parsed.docId}
            src={parsed.src}
            alt={imageMatch[1]}
          />
        );
        return;
      }
    }

    if (BULLET_RE.test(line)) {
      const item = line.replace(BULLET_RE, '');
      if (listBuf && !listBuf.ordered) {
        listBuf.items.push(item);
      } else {
        flushList();
        listBuf = { ordered: false, items: [item] };
      }
      return;
    }
    if (NUMBERED_RE.test(line)) {
      const item = line.replace(NUMBERED_RE, '');
      if (listBuf && listBuf.ordered) {
        listBuf.items.push(item);
      } else {
        flushList();
        listBuf = { ordered: true, items: [item] };
      }
      return;
    }
    flushList();

    if (line.startsWith('## ')) {
      blocks.push(
        <Typography
          key={idx}
          variant="h6"
          sx={{ fontSize: '1.15rem', fontWeight: 700, mt: 1.25 }}
        >
          {renderInline(line.slice(3))}
        </Typography>
      );
      return;
    }
    if (line.startsWith('# ')) {
      blocks.push(
        <Typography
          key={idx}
          variant="h4"
          sx={{ fontSize: '1.6rem', fontWeight: 700, mt: 2 }}
        >
          {renderInline(line.slice(2))}
        </Typography>
      );
      return;
    }
    blocks.push(
      <Typography key={idx} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {line ? renderInline(line) : ' '}
      </Typography>
    );
  });
  flushList();

  return <Box sx={{ '& > *': { mb: 0.5 } }}>{blocks}</Box>;
}

function getLineRange(value: string, start: number, end: number) {
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const tailIdx = end > start ? end - 1 : end;
  const nlAfter = value.indexOf('\n', tailIdx);
  const lineEnd = nlAfter === -1 ? value.length : nlAfter;
  return { lineStart, lineEnd };
}

function toggleListLines(block: string, ordered: boolean): string {
  const lines = block.split('\n');
  const re = ordered ? NUMBERED_RE : BULLET_RE;
  const allMatch = lines.every((l) => re.test(l));

  if (allMatch) {
    return lines.map((l) => l.replace(re, '')).join('\n');
  }

  let n = 1;
  return lines
    .map((l) => {
      const stripped = l.replace(BULLET_RE, '').replace(NUMBERED_RE, '');
      if (ordered) {
        const out = `${n}. ${stripped}`;
        n += 1;
        return out;
      }
      return `- ${stripped}`;
    })
    .join('\n');
}

export function handleListEnter(
  e: React.KeyboardEvent,
  value: string,
  onChange: (next: string) => void
): void {
  if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
  const el = e.target as HTMLTextAreaElement | HTMLInputElement;
  if (!el || (el.tagName !== 'TEXTAREA' && el.tagName !== 'INPUT')) return;
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  if (start !== end) return;

  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const line = value.slice(lineStart, start);

  const bulletMatch = line.match(/^- (.*)$/);
  const numberedMatch = line.match(/^(\d+)\. (.*)$/);
  if (!bulletMatch && !numberedMatch) return;

  e.preventDefault();

  const content = bulletMatch ? bulletMatch[1] : numberedMatch![2];
  if (content.length === 0) {
    const next = value.slice(0, lineStart) + value.slice(start);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(lineStart, lineStart);
    });
    return;
  }

  const prefix = bulletMatch
    ? '- '
    : `${parseInt(numberedMatch![1], 10) + 1}. `;
  const insertion = `\n${prefix}`;
  const next = value.slice(0, start) + insertion + value.slice(end);
  onChange(next);
  const caret = start + insertion.length;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(caret, caret);
  });
}

export function applyFormat(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  value: string,
  kind: FormatKind,
  onChange: (next: string) => void
): void {
  const start = el?.selectionStart ?? value.length;
  const end   = el?.selectionEnd   ?? value.length;

  if (kind === 'bold') {
    const selected = value.slice(start, end);
    const wrapped  = `**${selected}**`;
    const next     = value.slice(0, start) + wrapped + value.slice(end);
    onChange(next);
    const caret = selected.length > 0 ? start + wrapped.length : start + 2;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
    return;
  }

  if (kind === 'bullet' || kind === 'numbered') {
    const { lineStart, lineEnd } = getLineRange(value, start, end);
    const block = value.slice(lineStart, lineEnd);
    const newBlock = toggleListLines(block, kind === 'numbered');
    const next = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
    onChange(next);
    const caret = lineStart + newBlock.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
    return;
  }

  const prefix = kind === 'h1' ? '# ' : '## ';
  const lineStart = value.lastIndexOf('\n', start - 1) + 1;
  const nlAfter   = value.indexOf('\n', start);
  const lineEnd   = nlAfter === -1 ? value.length : nlAfter;
  const line      = value.slice(lineStart, lineEnd);
  const stripped  = line.replace(/^#{1,3}\s/, '');
  const newLine   = prefix + stripped;
  const next      = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
  onChange(next);
  const caret = lineStart + newLine.length;
  requestAnimationFrame(() => {
    el?.focus();
    el?.setSelectionRange(caret, caret);
  });
}

// Ids of documents inlined in a comment or ticket description by usePasteImage,
// which splices `![](/DownloadDocument?id=N)` into the text. Deleting one of those
// leaves a permanently broken image in the body, so the attachment row hides its
// delete button — removing the text is the way to remove the file.
export function inlinedDocumentIds(body: string | null | undefined): number[] {
  if (!body) return [];
  return Array.from(
    body.matchAll(/!\[[^\]]*\]\(\/DownloadDocument\?id=(\d+)\)/g),
    (m) => Number(m[1])
  );
}
